import { FC, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { Block, Build } from '@mui/icons-material'
import { Box, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material'

import { useGetTrackedEventQuery, useGetTrackedGameLiveEventArchetypesQuery } from '../../../../../api/core'
import { GRTooltip } from '../../../../../components/GRTooltip/GRTooltip'
import { endOfDayTimestamp } from '../../../../../helpers/endOfDayTimestamp'
import { useCurrentMarket } from '../../../../markets'
import { useTrackedEvents } from '../../../hooks/useTrackedEvents'
import { LiveEventEvolutionTypeEnum } from '../../../types/LiveEventCollection'
import { TrackedGame } from '../../../types/TrackedGame'
import { TrackingEvent } from '../../../types/TrackingEvents'
import { LiveEventEvolutionTimeline } from '../../LiveEventEvolutionTimeline/LiveEventEvolutionTimeline'
import './EvolutionTab.scss'

type EvolutionTabProps = {
  currentTrackedGame?: TrackedGame
  currentEventId?: string
  onEventSelect: (event: TrackingEvent) => void
  onScrollParentTop?: () => void
}

type SegmentToggle = 'first' | 'all' | 'last'

export const EvolutionTab: FC<EvolutionTabProps> = ({ currentEventId, currentTrackedGame, onEventSelect, onScrollParentTop }) => {
  const { t } = useTranslation()
  const [segmentToggle, setSegmentToggle] = useState<SegmentToggle>('first')
  const { currentMarketIso } = useCurrentMarket()
  const [showOnlyMechanicChanges, setShowOnlyMechanicChanges] = useState(false)
  const {
    data: eventData,
    isLoading: isLoadingEventData,
    isFetching: isFetchingEventData,
  } = useGetTrackedEventQuery(currentEventId || '', { skip: !currentEventId })
  const archetypeId = eventData?.event.archetypeEvolutionData?.archetypeId
  const {
    data: liveEventCollection,
    isLoading: isLoadingLiveEventCollection,
    isFetching: isFetchingLiveEventCollection,
  } = useGetTrackedGameLiveEventArchetypesQuery(
    {
      archetypeId: archetypeId || '',
      type: segmentToggle,
    },
    {
      skip: !archetypeId || !segmentToggle,
    }
  )
  const gameIds = useMemo(() => (currentTrackedGame ? [currentTrackedGame?.game.id] : []), [currentTrackedGame])
  const trackedEventsQuery = useTrackedEvents({ gameIds: gameIds, startTimestamp: 0, endTimestamp: endOfDayTimestamp(), marketIso: currentMarketIso })

  const filteredEvents = useMemo(() => {
    const events = trackedEventsQuery.data?.events || {}
    const uniqueEventsMap: { [eventId: string]: TrackingEvent } = {}

    if (!liveEventCollection) {
      return uniqueEventsMap
    }

    const evolutionEventIds = liveEventCollection.evolution?.map((evolution) => evolution.eventId) || []

    if (liveEventCollection.evolution?.length === 0) {
      return uniqueEventsMap
    }

    Object.values(events).forEach((gameEvents) => {
      gameEvents.forEach((event) => {
        if (evolutionEventIds.includes(event.eventId)) {
          uniqueEventsMap[event.eventId] = event
          // also index by eventId+start so the evolution timeline can look up the specific instance
          uniqueEventsMap[`${event.eventId}-${event.start}`] = event
        }
      })
    })

    return uniqueEventsMap
  }, [liveEventCollection, trackedEventsQuery.data?.events])

  const filteredEvolutionEntries = useMemo(() => {
    if (!liveEventCollection?.evolution) {
      return []
    }

    if (!showOnlyMechanicChanges) {
      return liveEventCollection.evolution
    }

    // Filter to show only entries that have MECHANIC_CHANGE type
    return liveEventCollection.evolution.filter((entry) => entry.types?.includes(LiveEventEvolutionTypeEnum.MechanicChange))
  }, [liveEventCollection?.evolution, showOnlyMechanicChanges])

  const archetypeName = useMemo(() => {
    return currentTrackedGame?.archetypes?.find((archetype) => archetype.archetypeId === archetypeId)?.names.en
  }, [currentTrackedGame, archetypeId])

  const isLoading = isLoadingEventData || isLoadingLiveEventCollection || isFetchingLiveEventCollection || isFetchingEventData

  const isDiscontinued = useMemo(() => {
    return currentTrackedGame?.archetypes?.find((archetype) => archetype.archetypeId === archetypeId)?.discontinued
  }, [archetypeId, currentTrackedGame?.archetypes])

  const handleSegmentToggle = (event: React.MouseEvent<HTMLElement>, value: SegmentToggle) => {
    if (value !== null) {
      setSegmentToggle(value)
    }
  }

  return (
    <Box className="EvolutionTab">
      {!isLoading && liveEventCollection && liveEventCollection.evolution && currentTrackedGame && (
        <Box className="ToolsContainer">
          <GRTooltip content={t('live-events:show_only_mechanic_changes')}>
            <ToggleButton
              value="check"
              className="MechanicChangesToggle"
              size="small"
              selected={showOnlyMechanicChanges}
              onClick={() => setShowOnlyMechanicChanges(!showOnlyMechanicChanges)}
            >
              <Build fontSize="small" />
            </ToggleButton>
          </GRTooltip>
          <ToggleButtonGroup className="SegmentToggle" value={segmentToggle} exclusive onChange={handleSegmentToggle} size="small">
            <GRTooltip content={<Trans i18nKey="live-events:show_by_first_event_tooltip" />}>
              <ToggleButton value="first">
                <Trans i18nKey="common:first" />
              </ToggleButton>
            </GRTooltip>
            <GRTooltip content={<Trans i18nKey="live-events:show_by_last_event_tooltip" />}>
              <ToggleButton value="last">
                <Trans i18nKey="common:latest" />
              </ToggleButton>
            </GRTooltip>
            <GRTooltip content={<Trans i18nKey="live-events:show_by_all_events_tooltip" />}>
              <ToggleButton value="all">
                <Trans i18nKey="common:all" />
              </ToggleButton>
            </GRTooltip>
          </ToggleButtonGroup>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        {isDiscontinued && (
          <GRTooltip content={t('live-events:live_event_evolution_type_discontinued')} styles={{ height: '20px', width: '20px', zIndex: 1000 }}>
            <Block color="error" fontSize="small" />
          </GRTooltip>
        )}
        <Typography variant="h6">{archetypeName}</Typography>
      </Box>

      <LiveEventEvolutionTimeline
        loading={isLoading}
        entries={filteredEvolutionEntries}
        onEventSelect={onEventSelect}
        events={filteredEvents as { [eventId: string]: TrackingEvent }}
        currentEventId={currentEventId}
        currentTrackedGame={currentTrackedGame}
        onScrollParentTop={onScrollParentTop}
      />
    </Box>
  )
}
